/**
 * One `index.html` serves every route, so without this map every URL on the
 * domain carried the same title. Google noticed: it discarded the tag and
 * wrote its own title for `/entrar` out of that screen's `h1`, which is what a
 * duplicate title always earns.
 *
 * Only the sales screen is meant to be found in a search. Everything else is
 * either a door for someone who already bought (`/entrar` and the password
 * screens) or a room behind the session guard, and a room behind a guard has
 * nothing to show a crawler but the shell.
 *
 * So `indexable` defaults to false and is granted once. A screen added
 * tomorrow is private by omission rather than public by omission — the same
 * direction the prompt catalogue's `tier` field defaults in.
 */
export const ORIGIN = 'https://roteirovisk.com';

/**
 * The static tag in `index.html` says the same thing, and `document.test.ts`
 * holds the two together — the crawler that runs JavaScript reads this one,
 * the crawler that does not reads that one.
 *
 * `max-image-preview:none` gives up the result thumbnail. The image Google had
 * to choose from is the share card, and a 1200×630 card with a 43px sentence
 * is a smudge at the size a result draws it.
 */
export const INDEXABLE_ROBOTS = 'index, follow, max-image-preview:none';

export interface RouteMeta {
  /** What the browser tab and the search result say. */
  title: string;
  /** Only the sales screen needs one: it is the only page a stranger lands on. */
  description?: string;
  indexable: boolean;
}

/**
 * The sales screen's own words, inside the ~160 characters a result snippet
 * shows. Every sentence exists on the screen verbatim, and `document.test.ts`
 * holds that to be true — a description that promises something the page does
 * not say is the search-result version of publicidade enganosa.
 *
 * It is one sentence because the screen's second one is 118 characters and
 * cannot be quoted in half: a snippet ending mid-promise is worse than a
 * shorter one.
 */
export const HOME_DESCRIPTION =
  '200 prompts para Reels, Carrossel e Stories e 100 roteiros que o gerador escreve na sua voz. Você troca o nicho e grava.';

/**
 * What someone types, and nothing else. "Roteirovisk" as a search term only
 * finds people who already know the name, and the result already prints
 * `roteirovisk.com` above the link — spending characters on the brand buys a
 * second copy of something on the same screen.
 *
 * Names both halves of what is sold, prompts and roteiros, over the surface
 * the catalogue actually covers. Broader than the formats it replaced, which
 * is the deliberate part: "redes sociais" reaches past Reels and Instagram,
 * and gives up their larger search volume to do it.
 */
export const HOME_TITLE = 'Gerador de prompts e roteiros para redes sociais';

const TITLES: Record<string, string> = {
  '/entrar': 'Entrar no Roteirovisk',
  '/entrar/senha': 'Entrar com senha · Roteirovisk',
  '/reenviar': 'Reenviar o acesso · Roteirovisk',
  '/senha/recuperar': 'Recuperar a senha · Roteirovisk',
  '/senha/definir': 'Definir a senha · Roteirovisk',
  '/acesso': 'Abrindo o seu acesso · Roteirovisk',
  '/biblioteca': 'Biblioteca de prompts · Roteirovisk',
  '/gerador': 'Gerador de prompts · Roteirovisk',
  '/gerador/perfil': 'O seu perfil · Roteirovisk',
  '/midia-kit': 'Os seus números · Roteirovisk',
  // Announced and unbuilt. They get a shell like every other route: what they
  // serve today is a question, and serving the sales markup at them would put
  // the offer's title in her tab on a screen that sells nothing.
  '/ideias': 'Minhas ideias · Roteirovisk',
  '/memorias': 'Minhas memórias · Roteirovisk',
  '/agentes': 'Meus agentes · Roteirovisk',
  '/gerador/montar': 'Gerar prompt · Roteirovisk',
  '/gerador/ia': 'Montador de prompt · Roteirovisk',
  '/gerador/fixados': 'Prompts fixados · Roteirovisk',
  // The two permanent redirects. They are named after where they are going,
  // so the tab does not blink the bare brand on the way.
  '/gerador/gerar': 'Gerar prompt · Roteirovisk',
  '/gerador/salvos': 'Prompts fixados · Roteirovisk',
  '/roteiros': 'Roteiros · Roteirovisk',
  '/analise': 'Análise de perfil · Roteirovisk',
  '/opiniao': 'Deixar uma opinião · Roteirovisk'
};

/**
 * The routes the build writes their own `index.html` for. Without them, every
 * one of these URLs is served the sales screen's prerendered markup as the
 * single-page fallback — 140 kB of a page the buyer is not on, and the sales
 * screen's title in her tab until React boots.
 */
export function staticShellRoutes(): { pathname: string; title: string }[] {
  return Object.entries(TITLES).map(([pathname, title]) => ({ pathname, title }));
}

export function metaFor(pathname: string): RouteMeta {
  if (pathname === '/') {
    return { title: HOME_TITLE, description: HOME_DESCRIPTION, indexable: true };
  }

  const exact = TITLES[pathname.replace(/\/+$/, '')];
  if (exact) {
    return { title: exact, indexable: false };
  }

  // `/biblioteca/:slug` and anything else the router draws. A prompt page is
  // one buyer's copy of what she paid for, so it is named after its section
  // rather than after itself.
  if (pathname.startsWith('/biblioteca/')) {
    return { title: TITLES['/biblioteca']!, indexable: false };
  }

  // `/gerador/fixados/:id`, named after the list it opened from for the same
  // reason.
  if (pathname.startsWith('/gerador/fixados/')) {
    return { title: TITLES['/gerador/fixados']!, indexable: false };
  }

  // Anything else is `NotFoundScreen`. Static hosting answers it 200 — the
  // single-page fallback has no way to know a route from a typo — so `noindex`
  // is what stops a mistyped address from being filed as a real page.
  return { title: NOT_FOUND_TITLE, indexable: false };
}

export const NOT_FOUND_TITLE = 'Página não encontrada · Roteirovisk';
